import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 6 — "Who else is in my company" (D-206-010 row 6, kept as value + link).
 *
 * **Who does what:** the person sees who else can see and work on his company; Raisy keeps the room
 * shared with exactly those people.
 * **What he sees:** how many people besides him have access, and who the first few are.
 * **What he feels:** *"my co-founder is in the same room"* — Maya's W9 want, the one the glowtest
 * gap analysis independently recorded as *"Team Settings/Admin Panel ❌ Missing"*. The count is the
 * answer to "am I alone in here?" and it is the whole point of the row.
 *
 * WHY IT LINKS RATHER THAN EDITS: adding or removing a person is an operation with its own screen
 * (`/team`), where the permission level is chosen and the consequence is explained. This page states
 * the state of the room and hands over — one place in the product owns granting access.
 *
 * ⚠️ THE COUNT AND "COULD NOT BE READ" ARE TWO DIFFERENT ANSWERS. The route requires viewer level,
 * so a teammate can read it too; but if the read fails, the section says so instead of printing
 * *"only you"*, because that sentence would be a claim about his company built on a failed request.
 */
export const CompanySection = ({ settings }: { settings: UserSettings }) => {
  const { permissions } = settings;

  const value =
    permissions.state === "loading" ? (
      <span className="text-muted-foreground">Reading who is in your company…</span>
    ) : permissions.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {permissions.reason}. This does not change who has access.
      </span>
    ) : permissions.value.length === 0 ? (
      <span>
        Only you
        <span className="ml-2 font-normal text-muted-foreground">
          nobody else has access to this company yet
        </span>
      </span>
    ) : (
      <span className="flex flex-col gap-1">
        <span>
          {permissions.value.length} {permissions.value.length === 1 ? "person besides you" : "people besides you"}
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          {permissions.value
            .slice(0, 4)
            .map((p) => p.name || p.email || p.user_id || "someone")
            .join(" · ")}
          {permissions.value.length > 4 ? ` · and ${permissions.value.length - 4} more` : ""}
        </span>
      </span>
    );

  return (
    <SettingsSection
      title="Who else is in my company"
      hint="The people who can see and work on this company beside you."
      value={value}
      link={{ to: "/team", label: "Open your team" }}
    />
  );
};
